import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import api, { VinBatchDetailResponse } from '../lib/api'

export default function VinBatchDetailPage() {
  const { batchId = '' } = useParams()
  const [data, setData] = useState<VinBatchDetailResponse | null>(null)
  const [customers, setCustomers] = useState<Array<{ id: string; name: string }>>([])
  const [selected, setSelected] = useState<string[]>([])
  const [convertCustomerId, setConvertCustomerId] = useState('')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = async () => {
    if (!batchId) return
    setLoading(true)
    setError(null)
    try {
      const response = await api.vinBatches.get(batchId)
      setData(response.data)
      setSelected([])
      setConvertCustomerId(response.data.batch.customerId || '')
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to load VIN batch detail')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [batchId])

  useEffect(() => {
    const hydrateCustomers = async () => {
      try {
        const response = await api.customers.list()
        setCustomers(response.data.map((item) => ({ id: item.id, name: item.name })))
      } catch {
        // Best-effort lookup hydration.
      }
    }
    void hydrateCustomers()
  }, [])

  const selectedItems = useMemo(() => {
    if (!data) return []
    const set = new Set(selected)
    return data.items.filter((item) => set.has(item.id))
  }, [data, selected])

  const sharedSelectedCustomerId = useMemo(() => {
    const ids = Array.from(new Set(selectedItems.map((item) => item.customerId).filter(Boolean))) as string[]
    return ids.length === 1 ? ids[0] : ''
  }, [selectedItems])

  const resolvedConvertCustomerId = convertCustomerId || data?.batch.customerId || sharedSelectedCustomerId || ''

  const allSelected = data && data.items.length > 0 && selected.length === data.items.length

  const toggle = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]))
  }

  const bulk = async (action: 'decode' | 'archive') => {
    if (!selected.length) return
    setSaving(true)
    setError(null)
    try {
      await api.vinInventory.bulkAction({ itemIds: selected, action })
      await load()
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Bulk action failed')
    } finally {
      setSaving(false)
    }
  }

  const convertSelectedToOrder = async () => {
    if (!selected.length) return
    if (!resolvedConvertCustomerId) {
      setError('Select a customer before converting these VINs to an order.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const response = await api.vinInventory.convertToOrder({
        itemIds: selected,
        customerId: resolvedConvertCustomerId,
        laneId: data?.batch.laneId || undefined,
      })
      await load()
      window.location.href = `/orders/${response.data.order.id}`
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to convert selected VINs to order')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="max-w-7xl mx-auto p-6 text-sm text-gray-500">Loading VIN batch...</div>

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">VIN Batch {data?.batch.batchNumber}</h1>
          <p className="text-sm text-gray-500 mt-1">Review parsed rows, duplicates/invalid input, and conversion readiness.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/vin-batches" className="border rounded px-3 py-2 text-sm">Back</Link>
          <Link to="/vin-inventory/import" className="bg-blue-700 text-white rounded px-3 py-2 text-sm">New Import</Link>
        </div>
      </div>

      {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <div className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
        <div><span className="font-semibold">Customer:</span> {data?.batch.customerName || '-'}</div>
        <div><span className="font-semibold">Lane:</span> {data?.batch.laneNumber || '-'}</div>
        <div><span className="font-semibold">Status:</span> {data?.batch.status || '-'}</div>
        <div><span className="font-semibold">Raw/Valid:</span> {data?.batch.rawCount || 0}/{data?.batch.validCount || 0}</div>
        <div><span className="font-semibold">Duplicates:</span> {data?.batch.duplicateCount || 0}</div>
        <div><span className="font-semibold">Invalid:</span> {data?.batch.invalidCount || 0}</div>
        <div><span className="font-semibold">Decoded:</span> {data?.summary.decoded_count ?? 0}</div>
        <div><span className="font-semibold">Failed Decode:</span> {data?.summary.failed_decode_count ?? 0}</div>
      </div>

      {!!data?.duplicateRows?.length && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm">
          <h2 className="font-semibold text-amber-900">Duplicate Input Rows</h2>
          <div className="mt-2 max-h-36 overflow-auto space-y-1 text-amber-800">
            {data.duplicateRows.slice(0, 100).map((row: any, idx: number) => (
              <div key={`${row.line}-${idx}`}>Line {row.line}: {row.rawInput} ({row.reason})</div>
            ))}
          </div>
        </div>
      )}

      {!!data?.invalidRows?.length && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm">
          <h2 className="font-semibold text-rose-900">Invalid Input Rows</h2>
          <div className="mt-2 max-h-36 overflow-auto space-y-1 text-rose-800">
            {data.invalidRows.slice(0, 100).map((row: any, idx: number) => (
              <div key={`${row.line}-${idx}`}>Line {row.line}: {row.rawInput} ({row.normalized || 'invalid'})</div>
            ))}
          </div>
        </div>
      )}

      {!!data && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="px-3 py-2 border-b flex items-center gap-2 text-sm">
            <label className="text-gray-600">Customer</label>
            <select
              className="border rounded px-2 py-1 text-sm min-w-[220px]"
              value={convertCustomerId}
              onChange={(event) => setConvertCustomerId(event.target.value)}
              disabled={saving}
            >
              <option value="">Select customer for conversion</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>{customer.name}</option>
              ))}
            </select>
            <button disabled={saving || !selected.length} onClick={() => void bulk('decode')} className="border rounded px-3 py-1">Retry Decode</button>
            <button disabled={saving || !selected.length} onClick={convertSelectedToOrder} className="bg-blue-700 text-white rounded px-3 py-1">Convert Selected to Order</button>
            <button disabled={saving || !selected.length} onClick={() => void bulk('archive')} className="border border-red-300 text-red-700 rounded px-3 py-1">Archive Selected</button>
            <span className="text-gray-500">Selected {selected.length}</span>
          </div>

          {!resolvedConvertCustomerId && selected.length > 0 && (
            <div className="px-3 py-2 border-b bg-amber-50 text-amber-800 text-sm">
              Select a customer before converting these VINs to an order.
            </div>
          )}

          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={!!allSelected}
                    onChange={() => setSelected(allSelected ? [] : (data?.items.map((item) => item.id) || []))}
                  />
                </th>
                <th className="px-3 py-2">VIN</th>
                <th className="px-3 py-2">Vehicle</th>
                <th className="px-3 py-2">Route</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Decode</th>
                <th className="px-3 py-2">Links</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item.id} className="border-t">
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={selected.includes(item.id)} onChange={() => toggle(item.id)} />
                  </td>
                  <td className="px-3 py-2 font-mono">{item.vin}</td>
                  <td className="px-3 py-2">{[item.year, item.make, item.model].filter(Boolean).join(' ') || '-'}</td>
                  <td className="px-3 py-2">{item.pickupZip || '-'} to {item.deliveryZip || '-'}</td>
                  <td className="px-3 py-2">{item.inventoryStatus}</td>
                  <td className="px-3 py-2">{item.decodeStatus}{item.decodeError ? ` (${item.decodeError})` : ''}</td>
                  <td className="px-3 py-2">
                    {item.orderId ? <Link className="text-blue-700 hover:underline" to={`/orders/${item.orderId}`}>{item.orderNumber || 'Order'}</Link> : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!!selectedItems.length && (
        <div className="text-xs text-gray-500">Selected VINs: {selectedItems.map((item) => item.vin).join(', ')}</div>
      )}
    </div>
  )
}
